import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from 'sonner';
import { 
  ShoppingCart, Menu, X, ChevronRight, Heart, Users, Clock, Package, 
  Leaf, Award, Mail, Phone, MapPin, ExternalLink, Check, Minus, Plus,
  Search, Filter, TrendingUp, AlertCircle, Gift, Trophy, Calendar,
  ArrowRight, Star, Sparkles, Target, Zap, HandHeart, Building2,
  Image, Camera, Upload
} from 'lucide-react';
import { Button } from './components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from './components/ui/card';
import { Badge } from './components/ui/badge';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Checkbox } from './components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetFooter } from './components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Progress } from './components/ui/progress';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './components/ui/accordion';
import { Separator } from './components/ui/separator';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// =============================================================================
// CONTEXT
// =============================================================================

const CartContext = createContext();

export const useCart = () => useContext(CartContext);

const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [products, setProducts] = useState([]);

  const addToCart = (product, quantity = 1) => {
    setCart(prev => {
      const existing = prev.find(item => item.product_id === product.id);
      if (existing) {
        return prev.map(item => 
          item.product_id === product.id 
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      return [...prev, { product_id: product.id, product, quantity }];
    });
    toast.success(`${product.name} added to basket`);
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    setCart(prev => prev.map(item => 
      item.product_id === productId ? { ...item, quantity } : item
    ));
  };

  const removeFromCart = (productId) => {
    setCart(prev => prev.filter(item => item.product_id !== productId));
  };

  const clearCart = () => setCart([]);

  const total = cart.reduce((sum, item) => sum + (item.product.price * item.quantity), 0);
  const itemCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const vgPoints = Math.floor(total);

  return (
    <CartContext.Provider value={{ 
      cart, products, setProducts, addToCart, updateQuantity, removeFromCart, clearCart, total, itemCount, vgPoints 
    }}>
      {children}
    </CartContext.Provider>
  );
};

// =============================================================================
// COMPONENTS
// =============================================================================

const Navbar = () => {
  const { itemCount } = useCart();
  const [isOpen, setIsOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { to: '/', label: 'Home' },
    { to: '/shop', label: 'Shop the Build' },
    { to: '/gallery', label: 'Gallery' },
    { to: '/about', label: 'About' },
    { to: '/impact', label: 'Impact' },
    { to: '/rewards', label: 'Rewards' },
  ];

  return (
    <nav className={`sticky top-0 z-50 transition-all duration-300 ${scrolled ? 'glass border-b border-gray-100/50 shadow-sm' : 'bg-transparent'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_vets-greenhouse-1/artifacts/5h1w5m88_download%20%287%29.jpeg" 
              alt="Veterans Garden Dumfries Logo"
              className="h-12 w-auto object-contain"
            />
            <div className="hidden sm:block">
              <span className="font-['Playfair_Display'] text-lg font-bold text-[#1F5F3A]">Veterans Garden</span>
              <span className="block text-xs text-[#C9A227] -mt-1 italic">Let's Grow Together</span>
            </div>
          </Link>

          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map(link => (
              <Link 
                key={link.to} 
                to={link.to} 
                className="text-[#4A6356] hover:text-[#1F5F3A] font-medium transition-colors"
                data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-4">
            <Sheet open={cartOpen} onOpenChange={setCartOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" size="icon" className="relative border-[#1F5F3A]/20 hover:bg-[#1F5F3A]/5" data-testid="cart-button">
                  <ShoppingCart className="w-5 h-5 text-[#1F5F3A]" />
                  {itemCount > 0 && (
                    <span className="absolute -top-2 -right-2 w-5 h-5 bg-[#C9A227] text-white text-xs rounded-full flex items-center justify-center font-bold">
                      {itemCount}
                    </span>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent className="w-full sm:max-w-md">
                <CartDrawer onClose={() => setCartOpen(false)} />
              </SheetContent>
            </Sheet>

            <button 
              className="lg:hidden p-2"
              onClick={() => setIsOpen(!isOpen)}
              data-testid="mobile-menu-button"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {isOpen && (
          <div className="lg:hidden pb-4 animate-fade-in">
            {navLinks.map(link => (
              <Link 
                key={link.to} 
                to={link.to} 
                className="block py-3 text-[#4A6356] hover:text-[#1F5F3A] font-medium border-b border-gray-100"
                onClick={() => setIsOpen(false)}
              >
                {link.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    </nav>
  );
};

const CartDrawer = ({ onClose }) => {
  const { cart, updateQuantity, removeFromCart, total, vgPoints } = useCart();
  const navigate = useNavigate();

  const handleCheckout = () => {
    onClose();
    navigate('/checkout');
  };

  return (
    <>
      <SheetHeader>
        <SheetTitle className="font-['Playfair_Display'] text-2xl text-[#1F5F3A]">Your Basket</SheetTitle>
      </SheetHeader>
      
      <div className="flex-1 overflow-auto py-4">
        {cart.length === 0 ? (
          <div className="text-center py-12">
            <ShoppingCart className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <p className="text-[#8C9E95]">Your basket is empty</p>
            <p className="text-sm text-[#8C9E95] mt-2">Add items to fund the greenhouse restoration</p>
          </div>
        ) : (
          <div className="space-y-4">
            {cart.map(item => (
              <div key={item.product_id} className="flex gap-4 p-3 bg-[#F7F9F7] rounded-lg" data-testid={`cart-item-${item.product_id}`}>
                <div className="w-16 h-16 bg-[#1F5F3A]/10 rounded-lg flex items-center justify-center">
                  <Package className="w-8 h-8 text-[#1F5F3A]" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-[#0A2F1F] text-sm">{item.product.name}</h4>
                  <p className="text-[#C9A227] font-bold">£{item.product.price.toFixed(2)}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <button 
                      onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                      className="w-7 h-7 rounded-full bg-white border border-gray-200 flex items-center justify-center hover:bg-gray-50"
                      data-testid={`decrease-qty-${item.product_id}`}
                    >
                      <Minus className="w-3 h-3" />
                    </button>
                    <span className="w-8 text-center font-semibold">{item.quantity}</span>
                    <button 
                      onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                      className="w-7 h-7 rounded-full bg-white border border-gray-200 flex items-center justify-center hover:bg-gray-50"
                      data-testid={`increase-qty-${item.product_id}`}
                    >
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>
                </div>
                <button 
                  onClick={() => removeFromCart(item.product_id)}
                  className="text-red-500 hover:text-red-700"
                  data-testid={`remove-item-${item.product_id}`}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {cart.length > 0 && (
        <SheetFooter className="flex-col gap-4 border-t pt-4">
          <div className="w-full space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-[#4A6356]">Subtotal</span>
              <span className="font-semibold">£{total.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-[#4A6356] flex items-center gap-1">
                <Award className="w-4 h-4 text-[#C9A227]" /> VG Points
              </span>
              <span className="font-semibold text-[#C9A227]">+{vgPoints}</span>
            </div>
          </div>
          <Button 
            className="w-full btn-primary"
            onClick={handleCheckout}
            data-testid="checkout-button"
          >
            Proceed to Checkout
          </Button>
        </SheetFooter>
      )}
    </>
  );
};

const ProgressBar = ({ stats }) => {
  const target = 4000000;
  const raised = stats?.total_raised || 0;
  const percentage = Math.min((raised / target) * 100, 100);
  
  const milestones = [
    { amount: 1000000, label: '£1M' },
    { amount: 2000000, label: '£2M' },
    { amount: 3000000, label: '£3M' },
    { amount: 4000000, label: '£4M' },
  ];

  return (
    <div className="w-full" data-testid="progress-bar">
      <div className="flex justify-between items-end mb-2">
        <div>
          <p className="text-sm text-[#4A6356]">Total Raised</p>
          <p className="text-3xl lg:text-4xl font-['Playfair_Display'] font-bold text-[#1F5F3A]">
            £{raised.toLocaleString()}
          </p>
        </div>
        <p className="text-sm text-[#8C9E95]">of £{(target / 1000000).toFixed(0)}M target</p>
      </div>
      
      <div className="relative h-4 bg-gray-200 rounded-full overflow-hidden">
        <div 
          className="absolute inset-y-0 left-0 progress-gradient rounded-full transition-all duration-1000"
          style={{ width: `${percentage}%` }}
        />
        {milestones.map(m => (
          <div 
            key={m.amount}
            className="absolute top-0 h-full w-0.5 bg-white/50"
            style={{ left: `${(m.amount / target) * 100}%` }}
          />
        ))}
      </div>
      
      <div className="flex justify-between mt-2">
        {milestones.map(m => (
          <span 
            key={m.amount} 
            className={`text-xs ${raised >= m.amount ? 'text-[#1F5F3A] font-semibold' : 'text-[#8C9E95]'}`}
          >
            {m.label}
          </span>
        ))}
      </div>
    </div>
  );
};

const ProductCard = ({ product }) => {
  const { addToCart } = useCart();
  const [quantity, setQuantity] = useState(1);

  return (
    <Card className="shop-card bg-white border-gray-100 overflow-hidden group" data-testid={`product-card-${product.id}`}>
      <div className="aspect-[4/3] bg-gradient-to-br from-[#F7F9F7] to-[#E8F0EB] flex items-center justify-center relative overflow-hidden">
        {product.image_url ? (
          <img 
            src={product.image_url} 
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <Package className="w-16 h-16 text-[#1F5F3A]/30 group-hover:text-[#1F5F3A]/50 transition-colors" />
        )}
        {product.is_urgent && (
          <Badge className="absolute top-3 right-3 bg-[#C9A227] text-white">
            <AlertCircle className="w-3 h-3 mr-1" /> Urgent
          </Badge>
        )}
      </div>
      <CardContent className="p-4">
        <Badge variant="outline" className="mb-2 text-xs border-[#3FA06A] text-[#3FA06A] capitalize">
          {product.category}
        </Badge>
        <h3 className="font-semibold text-[#0A2F1F] mb-1 line-clamp-1">{product.name}</h3>
        <p className="text-sm text-[#8C9E95] mb-3 line-clamp-2">{product.description}</p>
        <div className="flex items-center justify-between">
          <p className="text-xl font-bold text-[#C9A227]">£{product.price.toFixed(2)}</p>
          <div className="flex items-center gap-2">
            <div className="flex items-center border rounded-full">
              <button 
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-l-full"
              >
                <Minus className="w-3 h-3" />
              </button>
              <span className="w-8 text-center text-sm font-semibold">{quantity}</span>
              <button 
                onClick={() => setQuantity(quantity + 1)}
                className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-r-full"
              >
                <Plus className="w-3 h-3" />
              </button>
            </div>
          </div>
        </div>
      </CardContent>
      <CardFooter className="p-4 pt-0">
        <Button 
          className="w-full btn-primary text-sm py-2"
          onClick={() => {
            addToCart(product, quantity);
            setQuantity(1);
          }}
          data-testid={`add-to-cart-${product.id}`}
        >
          Add to Basket
        </Button>
      </CardFooter>
    </Card>
  );
};

const ImpactStats = ({ stats }) => {
  const statItems = [
    { icon: Heart, label: 'Total Donors', value: stats?.total_donors || 0, color: 'text-red-500' },
    { icon: Package, label: 'Items Funded', value: stats?.items_funded || 0, color: 'text-[#1F5F3A]' },
    { icon: Award, label: 'Panes Funded', value: stats?.panes_funded || 0, color: 'text-[#3FA06A]' },
    { icon: Clock, label: 'Hours Funded', value: stats?.hours_funded || 0, color: 'text-[#C9A227]' },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="impact-stats">
      {statItems.map(stat => (
        <div key={stat.label} className="bg-[#F0F5F2] rounded-2xl p-4 lg:p-6 border border-[#1F5F3A]/10">
          <stat.icon className={`w-6 h-6 ${stat.color} mb-2`} />
          <p className="text-2xl lg:text-3xl font-['Playfair_Display'] font-bold text-[#0A2F1F]">
            {stat.value.toLocaleString()}
          </p>
          <p className="text-sm text-[#4A6356]">{stat.label}</p>
        </div>
      ))}
    </div>
  );
};

const DonorTicker = ({ donors }) => {
  if (!donors || donors.length === 0) return null;

  const displayDonors = [...donors, ...donors];

  return (
    <div className="overflow-hidden bg-[#1F5F3A]/5 py-3 border-y border-[#1F5F3A]/10">
      <div className="ticker-animate flex gap-8 whitespace-nowrap">
        {displayDonors.map((donor, i) => (
          <div key={i} className="flex items-center gap-2 text-sm">
            <Heart className="w-4 h-4 text-[#C9A227]" />
            <span className="font-semibold text-[#1F5F3A]">{donor.donor_name}</span>
            <span className="text-[#8C9E95]">donated £{donor.total_amount?.toFixed(2)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const Footer = () => (
  <footer className="bg-[#1F5F3A] text-white py-12">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid md:grid-cols-4 gap-8">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_vets-greenhouse-1/artifacts/5h1w5m88_download%20%287%29.jpeg" 
              alt="Veterans Garden Dumfries Logo"
              className="h-16 w-auto object-contain bg-white rounded-lg p-1"
            />
          </div>
          <p className="text-[#C9A227] font-semibold italic mb-2">Let's Grow Together</p>
          <p className="text-white/70 text-sm mb-2">
            Restoring heritage, growing community, supporting veterans in Dumfries & Galloway.
          </p>
          <p className="text-white/50 text-xs">
            Scottish Charity No. SC048351
          </p>
        </div>
        
        <div>
          <h4 className="font-semibold mb-4">Quick Links</h4>
          <div className="space-y-2 text-sm text-white/70">
            <Link to="/shop" className="block hover:text-white transition-colors">Shop the Build</Link>
            <Link to="/gallery" className="block hover:text-white transition-colors">Gallery</Link>
            <Link to="/about" className="block hover:text-white transition-colors">About Us</Link>
            <Link to="/impact" className="block hover:text-white transition-colors">Impact Tracker</Link>
          </div>
        </div>
        
        <div>
          <h4 className="font-semibold mb-4">Contact</h4>
          <div className="space-y-2 text-sm text-white/70">
            <p className="flex items-center gap-2"><MapPin className="w-4 h-4" /> Dumfries & Galloway, Scotland</p>
            <p className="flex items-center gap-2"><Mail className="w-4 h-4" /> veteransgarden@outlook.com</p>
            <p className="flex items-center gap-2"><Phone className="w-4 h-4" /> 07592 450871</p>
          </div>
        </div>
        
        <div>
          <h4 className="font-semibold mb-4">Support</h4>
          <div className="space-y-2 text-sm text-white/70">
            <Link to="/corporate" className="block hover:text-white transition-colors">Corporate Sponsorship</Link>
            <Link to="/contact" className="block hover:text-white transition-colors">Get in Touch</Link>
            <Link to="/rewards" className="block hover:text-white transition-colors">VG Points</Link>
            <Link to="/admin" className="block hover:text-white transition-colors">Admin</Link>
          </div>
        </div>
      </div>
      
      <Separator className="my-8 bg-white/20" />
      
      <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-white/50">
        <p>© 2024 Veterans Garden Greenhouse. Scottish Charity No. SC048351</p>
        <div className="flex gap-4">
          <a href="#" className="hover:text-white transition-colors">Privacy Policy</a>
          <a href="#" className="hover:text-white transition-colors">Terms</a>
        </div>
      </div>
    </div>
  </footer>
);

// =============================================================================
// PAGES
// =============================================================================

const HomePage = () => {
  const [stats, setStats] = useState(null);
  const [donors, setDonors] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, donorsRes] = await Promise.all([
          axios.get(`${API}/impact`),
          axios.get(`${API}/recent-donors?limit=10`)
        ]);
        setStats(statsRes.data);
        setDonors(donorsRes.data);
      } catch (e) {
        console.error('Error fetching data:', e);
      }
    };
    fetchData();
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[80vh] flex items-center overflow-hidden grain-overlay">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_vets-greenhouse-1/artifacts/mzkjg34o_IMG-20251210-WA0000.jpg')`,
            filter: 'brightness(0.35)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1F5F3A]/90 to-[#1F5F3A]/60" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-2xl animate-slide-up">
            <Badge className="mb-6 bg-[#C9A227] text-white px-4 py-1">
              <Sparkles className="w-4 h-4 mr-2" /> Let's Grow Together
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-['Playfair_Display'] font-bold text-white leading-tight mb-6">
              Help us build the Veterans Garden Greenhouse
            </h1>
            <p className="text-xl text-white/80 mb-4">
              Pane by pane, bolt by bolt. Fund the restoration of this historic 90m x 30m listed-building greenhouse in Dumfries & Galloway.
            </p>
            <p className="text-lg text-white/70 mb-8">
              Your donations fund building materials, labour costs, and everything needed to bring this greenhouse back to life.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                className="btn-accent text-lg px-8 py-4"
                onClick={() => navigate('/shop')}
                data-testid="shop-cta-button"
              >
                Shop the Build <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                variant="outline"
                className="bg-transparent text-white border-white hover:bg-white/10 rounded-full px-8 py-4"
                onClick={() => navigate('/about')}
              >
                Learn More
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Donor Ticker */}
      <DonorTicker donors={donors} />

      {/* Progress Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <ProgressBar stats={stats} />
            <p className="text-center mt-6 text-[#4A6356]">
              <Award className="w-5 h-5 inline mr-2 text-[#C9A227]" />
              Every £1 grows the greenhouse and earns you <strong>VG Points</strong>
            </p>
          </div>
        </div>
      </section>

      {/* Impact Stats */}
      <section className="py-16 bg-[#F7F9F7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-['Playfair_Display'] font-bold text-[#1F5F3A] text-center mb-12">
            Our Collective Impact
          </h2>
          <ImpactStats stats={stats} />
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-['Playfair_Display'] font-bold text-[#1F5F3A] text-center mb-4">
            How It Works
          </h2>
          <p className="text-[#4A6356] text-center mb-12 max-w-2xl mx-auto">
            Your donation funds real materials and labour. We purchase and use them on-site.
          </p>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: ShoppingCart, title: 'Choose Items', desc: 'Browse our catalog of panes, tools, labour hours, and bundles' },
              { icon: Heart, title: 'Fund the Build', desc: 'Complete your donation securely. Every £1 = 1 VG Point' },
              { icon: Zap, title: 'Watch Progress', desc: 'Track how your contribution helps restore the greenhouse' },
            ].map((step, i) => (
              <div key={i} className="text-center">
                <div className="w-16 h-16 rounded-full bg-[#1F5F3A] mx-auto mb-4 flex items-center justify-center">
                  <step.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-semibold text-[#0A2F1F] mb-2">{step.title}</h3>
                <p className="text-[#4A6356] text-sm">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#1F5F3A] relative grain-overlay">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-['Playfair_Display'] font-bold text-white mb-6">
            Ready to Make a Difference?
          </h2>
          <p className="text-white/80 mb-8 text-lg">
            Join {stats?.total_donors || 0}+ supporters who are rebuilding history together.
          </p>
          <Button 
            className="btn-accent text-lg px-10 py-4 animate-pulse-gold"
            onClick={() => navigate('/shop')}
          >
            Start Funding Now
          </Button>
        </div>
      </section>
    </div>
  );
};

const ShopPage = () => {
  const { products, setProducts, addToCart } = useCart();
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('default');
  const [urgentOnly, setUrgentOnly] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, categoriesRes] = await Promise.all([
          axios.get(`${API}/products`),
          axios.get(`${API}/categories`)
        ]);
        setProducts(productsRes.data);
        setCategories(categoriesRes.data);
      } catch (e) {
        console.error('Error fetching products:', e);
        toast.error('Failed to load products');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [setProducts]);

  const filteredProducts = products
    .filter(p => selectedCategory === 'all' || p.category === selectedCategory)
    .filter(p => !urgentOnly || p.is_urgent)
    .sort((a, b) => {
      if (sortBy === 'price-low') return a.price - b.price;
      if (sortBy === 'price-high') return b.price - a.price;
      if (sortBy === 'urgent') return (b.is_urgent ? 1 : 0) - (a.is_urgent ? 1 : 0);
      return 0;
    });

  return (
    <div className="min-h-screen bg-[#F7F9F7]" data-testid="shop-page">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="text-3xl lg:text-4xl font-['Playfair_Display'] font-bold text-[#1F5F3A] mb-2">
            Shop the Build
          </h1>
          <p className="text-[#4A6356] mb-4">
            You're funding these items; we will purchase and use them on the build.
          </p>
          {/* Disclaimer */}
          <div className="bg-[#F0F5F2] border border-[#1F5F3A]/20 rounded-lg p-4">
            <p className="text-sm text-[#4A6356]">
              <strong className="text-[#1F5F3A]">Please note:</strong> By selecting items, you are making a donation towards the Veterans Garden Greenhouse restoration. 
              You will not receive physical products — your generous contribution funds the materials, equipment, and labour needed to complete the build. 
              A certificate acknowledging your support will be emailed to you.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="flex flex-col lg:flex-row gap-4 mb-8">
          <div className="flex-1 flex flex-wrap gap-2">
            <Button
              variant={selectedCategory === 'all' ? 'default' : 'outline'}
              className={selectedCategory === 'all' ? 'bg-[#1F5F3A]' : ''}
              onClick={() => setSelectedCategory('all')}
              data-testid="filter-all"
            >
              All Items
            </Button>
            {categories.map(cat => (
              <Button
                key={cat.id}
                variant={selectedCategory === cat.id ? 'default' : 'outline'}
                className={selectedCategory === cat.id ? 'bg-[#1F5F3A]' : ''}
                onClick={() => setSelectedCategory(cat.id)}
                data-testid={`filter-${cat.id}`}
              >
                {cat.name}
              </Button>
            ))}
          </div>
          
          <div className="flex gap-2 items-center">
            <Button
              variant={urgentOnly ? 'default' : 'outline'}
              className={urgentOnly ? 'bg-[#C9A227] hover:bg-[#B08D21]' : ''}
              onClick={() => setUrgentOnly(!urgentOnly)}
              data-testid="filter-urgent"
            >
              <AlertCircle className="w-4 h-4 mr-2" /> Urgent Needs
            </Button>
            
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[180px]" data-testid="sort-select">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">Default</SelectItem>
                <SelectItem value="price-low">Price: Low to High</SelectItem>
                <SelectItem value="price-high">Price: High to Low</SelectItem>
                <SelectItem value="urgent">Urgent First</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <Card key={i} className="animate-pulse">
                <div className="aspect-[4/3] bg-gray-200" />
                <CardContent className="p-4 space-y-3">
                  <div className="h-4 bg-gray-200 rounded w-1/3" />
                  <div className="h-5 bg-gray-200 rounded" />
                  <div className="h-4 bg-gray-200 rounded w-2/3" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredProducts.map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}

        {!loading && filteredProducts.length === 0 && (
          <div className="text-center py-12">
            <Package className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <p className="text-[#8C9E95]">No products found matching your filters</p>
          </div>
        )}
      </div>
    </div>
  );
};

const CheckoutPage = () => {
  const { cart, total, vgPoints, clearCart } = useCart();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    donor_name: '',
    donor_email: '',
    gift_aid: false,
    gift_aid_address: '',
    gift_aid_postcode: '',
    dedication: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (cart.length === 0) {
      toast.error('Your basket is empty');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/checkout/stripe`, {
        items: cart.map(item => ({ product_id: item.product_id, quantity: item.quantity })),
        ...formData,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe
      window.location.href = response.data.checkout_url;
    } catch (e) {
      console.error('Checkout error:', e);
      toast.error('Failed to process checkout. Please try again.');
      setLoading(false);
    }
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-[#F7F9F7] flex items-center justify-center" data-testid="checkout-empty">
        <div className="text-center">
          <ShoppingCart className="w-20 h-20 mx-auto text-gray-300 mb-6" />
          <h2 className="text-2xl font-['Playfair_Display'] font-bold text-[#1F5F3A] mb-4">Your basket is empty</h2>
          <p className="text-[#4A6356] mb-6">Add some items to start funding the restoration</p>
          <Button className="btn-primary" onClick={() => navigate('/shop')}>
            Browse Items
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F9F7] py-12" data-testid="checkout-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-['Playfair_Display'] font-bold text-[#1F5F3A] mb-4">Checkout</h1>
        
        {/* Disclaimer */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-8">
          <p className="text-sm text-amber-800">
            <strong>Reminder:</strong> This is a donation — you will not receive physical items. Your contribution funds the greenhouse restoration.
          </p>
        </div>
        
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Form */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="font-['Playfair_Display']">Your Details</CardTitle>
                <CardDescription>We'll send your certificate and receipt to your email</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Full Name *</Label>
                      <Input 
                        id="name"
                        value={formData.donor_name}
                        onChange={e => setFormData({...formData, donor_name: e.target.value})}
                        required
                        placeholder="Your name"
                        data-testid="input-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input 
                        id="email"
                        type="email"
                        value={formData.donor_email}
                        onChange={e => setFormData({...formData, donor_email: e.target.value})}
                        required
                        placeholder="your@email.com"
                        data-testid="input-email"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="dedication">Dedication Message (Optional)</Label>
                    <Input 
                      id="dedication"
                      value={formData.dedication}
                      onChange={e => setFormData({...formData, dedication: e.target.value})}
                      placeholder="In memory of... / On behalf of..."
                      data-testid="input-dedication"
                    />
                  </div>

                  <Separator />

                  {/* Gift Aid */}
                  <div className="bg-[#F0F5F2] p-4 rounded-lg">
                    <div className="flex items-start gap-3">
                      <Checkbox 
                        id="gift-aid"
                        checked={formData.gift_aid}
                        onCheckedChange={v => setFormData({...formData, gift_aid: v})}
                        data-testid="checkbox-gift-aid"
                      />
                      <div>
                        <Label htmlFor="gift-aid" className="font-semibold text-[#1F5F3A] cursor-pointer">
                          Add Gift Aid to my donation (UK taxpayers only)
                        </Label>
                        <p className="text-sm text-[#4A6356] mt-1">
                          Boost your donation by 25% at no extra cost. I confirm I am a UK taxpayer and understand that if I pay less Income Tax and/or Capital Gains Tax than the amount of Gift Aid claimed, it is my responsibility to pay any difference.
                        </p>
                      </div>
                    </div>

                    {formData.gift_aid && (
                      <div className="grid sm:grid-cols-2 gap-4 mt-4 animate-fade-in">
                        <div>
                          <Label htmlFor="address">Home Address *</Label>
                          <Input 
                            id="address"
                            value={formData.gift_aid_address}
                            onChange={e => setFormData({...formData, gift_aid_address: e.target.value})}
                            required={formData.gift_aid}
                            placeholder="Your address"
                            data-testid="input-gift-aid-address"
                          />
                        </div>
                        <div>
                          <Label htmlFor="postcode">Postcode *</Label>
                          <Input 
                            id="postcode"
                            value={formData.gift_aid_postcode}
                            onChange={e => setFormData({...formData, gift_aid_postcode: e.target.value})}
                            required={formData.gift_aid}
                            placeholder="AB1 2CD"
                            data-testid="input-gift-aid-postcode"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full btn-primary text-lg py-6"
                    disabled={loading}
                    data-testid="submit-checkout"
                  >
                    {loading ? 'Processing...' : `Pay £${total.toFixed(2)} with Stripe`}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Order Summary */}
          <div>
            <Card className="sticky top-24">
              <CardHeader>
                <CardTitle className="font-['Playfair_Display']">Funding Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {cart.map(item => (
                  <div key={item.product_id} className="flex justify-between text-sm">
                    <span className="text-[#4A6356]">{item.product.name} x{item.quantity}</span>
                    <span className="font-semibold">£{(item.product.price * item.quantity).toFixed(2)}</span>
                  </div>
                ))}
                
                <Separator />
                
                <div className="flex justify-between">
                  <span className="text-[#4A6356]">Subtotal</span>
                  <span className="font-semibold">£{total.toFixed(2)}</span>
                </div>
                
                {formData.gift_aid && (
                  <div className="flex justify-between text-[#3FA06A]">
                    <span>Gift Aid (25%)</span>
                    <span>+£{(total * 0.25).toFixed(2)}</span>
                  </div>
                )}
                
                <Separator />
                
                <div className="flex justify-between text-lg font-bold">
                  <span>Total Impact</span>
                  <span className="text-[#1F5F3A]">
                    £{formData.gift_aid ? (total * 1.25).toFixed(2) : total.toFixed(2)}
                  </span>
                </div>
                
                <div className="bg-[#C9A227]/10 p-3 rounded-lg">
                  <div className="flex items-center gap-2 text-[#C9A227]">
                    <Award className="w-5 h-5" />
                    <span className="font-semibold">You'll earn {vgPoints} VG Points</span>
                  </div>
                  <p className="text-xs text-[#4A6356] mt-1">
                    Redeem for plants & coffee when we open!
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [donation, setDonation] = useState(null);
  const { clearCart } = useCart();
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const checkPayment = async () => {
      if (!sessionId) {
        setStatus('error');
        return;
      }

      try {
        // Poll for payment status
        let attempts = 0;
        const maxAttempts = 5;
        
        const poll = async () => {
          const response = await axios.get(`${API}/checkout/status/${sessionId}`);
          
          if (response.data.payment_status === 'paid') {
            setStatus('success');
            clearCart();
            
            // Get donation details from metadata
            const donationId = response.data.metadata?.donation_id;
            if (donationId) {
              const donationRes = await axios.get(`${API}/donations/${donationId}`);
              setDonation(donationRes.data);
            }
          } else if (attempts < maxAttempts) {
            attempts++;
            setTimeout(poll, 2000);
          } else {
            setStatus('pending');
          }
        };
        
        await poll();
      } catch (e) {
        console.error('Error checking payment:', e);
        setStatus('error');
      }
    };

    checkPayment();
  }, [sessionId, clearCart]);

  const downloadCertificate = async () => {
    if (!donation?.id) return;
    
    try {
      const response = await axios.get(`${API}/donations/${donation.id}/certificate`);
      const { pdf_base64, filename } = response.data;
      
      const link = document.createElement('a');
      link.href = `data:application/pdf;base64,${pdf_base64}`;
      link.download = filename;
      link.click();
      
      toast.success('Certificate downloaded!');
    } catch (e) {
      toast.error('Failed to download certificate');
    }
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-[#F7F9F7] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#1F5F3A] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#4A6356]">Confirming your donation...</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-[#F7F9F7] flex items-center justify-center">
        <div className="text-center">
          <X className="w-20 h-20 mx-auto text-red-500 mb-6" />
          <h2 className="text-2xl font-['Playfair_Display'] font-bold text-[#1F5F3A] mb-4">Something went wrong</h2>
          <p className="text-[#4A6356] mb-6">We couldn't confirm your payment. Please contact support.</p>
          <Button className="btn-primary" onClick={() => window.location.href = '/shop'}>
            Return to Shop
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F9F7] py-12" data-testid="checkout-success">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <Card className="text-center">
          <CardContent className="pt-12 pb-8">
            <div className="w-20 h-20 rounded-full bg-[#3FA06A] mx-auto mb-6 flex items-center justify-center animate-pulse-gold">
              <Check className="w-10 h-10 text-white" />
            </div>
            
            <h1 className="text-3xl font-['Playfair_Display'] font-bold text-[#1F5F3A] mb-4">
              Thank You, {donation?.donor_name || 'Restorer'}!
            </h1>
            
            <p className="text-[#4A6356] mb-6">
              Your donation of <strong className="text-[#C9A227]">£{donation?.total_amount?.toFixed(2) || '0.00'}</strong> is helping restore the Veterans Garden Greenhouse.
            </p>

            {donation && (
              <div className="bg-[#F0F5F2] p-6 rounded-xl mb-6 text-left">
                <h3 className="font-semibold text-[#1F5F3A] mb-3">Your Contribution</h3>
                <div className="space-y-2 text-sm">
                  {donation.items?.slice(0, 5).map((item, i) => (
                    <div key={i} className="flex justify-between">
                      <span className="text-[#4A6356]">{item.name} x{item.quantity}</span>
                      <span className="font-semibold">£{item.subtotal?.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                
                <Separator className="my-4" />
                
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2 text-[#C9A227]">
                    <Award className="w-5 h-5" />
                    <span className="font-semibold">{donation.vg_points} VG Points earned</span>
                  </div>
                </div>
                
                <p className="text-xs text-[#8C9E95] mt-3">
                  Certificate ID: {donation.certificate_id}
                </p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                className="btn-primary"
                onClick={downloadCertificate}
                data-testid="download-certificate"
              >
                <Gift className="w-4 h-4 mr-2" /> Download Certificate
              </Button>
              <Button 
                variant="outline"
                onClick={() => window.location.href = '/shop'}
              >
                Continue Shopping
              </Button>
            </div>

            {/* Email spam notice */}
            <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-amber-800 text-left">
                  <strong>Check your inbox!</strong> A confirmation email with your certificate is on its way. 
                  Can't find it? <span className="font-semibold">Please check your spam/junk folder</span> and mark us as safe.
                </p>
              </div>
            </div>

            <Separator className="my-8" />

            {/* Social sharing section */}
            <div>
              <h3 className="font-semibold text-[#1F5F3A] mb-2">Spread the word! 📣</h3>
              <p className="text-sm text-[#4A6356] mb-4">Help us reach more supporters by sharing your contribution</p>
              
              <div className="grid sm:grid-cols-2 gap-3 mb-4">
                <Button 
                  variant="outline" 
                  className="w-full justify-start gap-2 hover:bg-[#1DA1F2]/10 hover:border-[#1DA1F2]"
                  onClick={() => {
                    const text = `🌱 I just donated £${donation?.total_amount?.toFixed(2) || ''} to help restore the Veterans Garden Greenhouse in Dumfries! This historic building will become a sanctuary for veterans. Join me in supporting this amazing cause 💚\n\n${window.location.origin}`;
                    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`, '_blank');
                  }}
                  data-testid="share-twitter"
                >
                  <span className="font-bold">𝕏</span> Share on X / Twitter
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start gap-2 hover:bg-[#1877F2]/10 hover:border-[#1877F2]"
                  onClick={() => {
                    const text = `I just donated to help restore the Veterans Garden Greenhouse! 🌱 This historic building in Dumfries will become a sanctuary supporting our veteran community. Every £1 donated earns VG Points for the future plant & coffee shop. Join me!`;
                    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.origin)}&quote=${encodeURIComponent(text)}`, '_blank');
                  }}
                  data-testid="share-facebook"
                >
                  <span className="text-[#1877F2] font-bold">f</span> Share on Facebook
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start gap-2 hover:bg-[#25D366]/10 hover:border-[#25D366]"
                  onClick={() => {
                    const text = `🌱 Hey! I just donated to the Veterans Garden Greenhouse restoration project. It's a beautiful cause - restoring a historic greenhouse to create a sanctuary for veterans in Dumfries. Plus, every £1 = 1 VG Point for their future plant shop! Check it out: ${window.location.origin}`;
                    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
                  }}
                  data-testid="share-whatsapp"
                >
                  <span className="text-[#25D366] font-bold">●</span> Share on WhatsApp
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start gap-2 hover:bg-[#0A66C2]/10 hover:border-[#0A66C2]"
                  onClick={() => {
                    const url = window.location.origin;
                    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank');
                  }}
                  data-testid="share-linkedin"
                >
                  <span className="text-[#0A66C2] font-bold">in</span> Share on LinkedIn
                </Button>
              </div>

              {/* Copy message button */}
              <Button 
                variant="ghost" 
                size="sm"
                className="text-[#4A6356] hover:text-[#1F5F3A]"
                onClick={() => {
                  const message = `🌱 I just donated to help restore the Veterans Garden Greenhouse in Dumfries!\n\nThis historic building will become a sanctuary for our veteran community, offering therapeutic gardening and a welcoming space.\n\n💚 Every £1 donated = 1 VG Point (redeemable at their future plant & coffee shop!)\n\nJoin me in supporting this amazing cause: ${window.location.origin}\n\n#VeteransGarden #LetsGrowTogether #Dumfries`;
                  navigator.clipboard.writeText(message);
                  toast.success('Message copied to clipboard!');
                }}
                data-testid="copy-share-message"
              >
                📋 Copy share message
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const AboutPage = () => (
  <div className="min-h-screen bg-[#F7F9F7]" data-testid="about-page">
    <div className="bg-[#1F5F3A] text-white py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Badge className="mb-4 bg-[#C9A227]">Our Story</Badge>
        <h1 className="text-4xl lg:text-5xl font-['Playfair_Display'] font-bold mb-4">
          About the Project
        </h1>
        <p className="text-xl text-white/80 max-w-2xl">
          Restoring a piece of history while supporting our veteran community
        </p>
      </div>
    </div>

    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
        <div>
          <h2 className="text-3xl font-['Playfair_Display'] font-bold text-[#1F5F3A] mb-6">
            A Listed Building Awaiting Restoration
          </h2>
          <p className="text-[#4A6356] mb-4">
            The Veterans Garden Greenhouse is a magnificent 90m x 30m structure nestled in the beautiful Dumfries & Galloway region of Scotland. As a listed building, it holds significant historical and architectural value - but as you can see from these photos, it urgently needs restoration.
          </p>
          <p className="text-[#4A6356] mb-4">
            Our mission is to restore this heritage structure to its former glory, creating a space where veterans can find peace, purpose, and community through horticulture and nature.
          </p>
          <p className="text-[#4A6356]">
            With your help, we're not just restoring glass and steel – we're building hope, one pane at a time. Every donation directly funds the materials, equipment, and skilled labour needed for this vital restoration work.
          </p>
        </div>
        <div className="aspect-video rounded-xl overflow-hidden">
          <img 
            src="https://customer-assets.emergentagent.com/job_vets-greenhouse-1/artifacts/dh9veqgm_IMG-20251210-WA0003.jpg"
            alt="Current state of the Veterans Garden Greenhouse awaiting restoration"
            className="w-full h-full object-cover"
          />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-8 mb-16">
        <div className="aspect-video rounded-xl overflow-hidden">
          <img 
            src="https://customer-assets.emergentagent.com/job_vets-greenhouse-1/artifacts/mzkjg34o_IMG-20251210-WA0000.jpg"
            alt="Damaged greenhouse frame structure"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="aspect-video rounded-xl overflow-hidden">
          <img 
            src="https://customer-assets.emergentagent.com/job_vets-greenhouse-1/artifacts/0cmta4sv_IMG-20251210-WA0004.jpg"
            alt="Greenhouse restoration site"
            className="w-full h-full object-cover"
          />
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-8 mb-16">
        {[
          { icon: Building2, title: 'Heritage Status', desc: 'Category B listed building, protected for its architectural significance' },
          { icon: Users, title: 'Community Focus', desc: 'Creating meaningful opportunities for veterans and local residents' },
          { icon: Leaf, title: 'Sustainable Future', desc: 'Growing plants, food, and wellbeing for generations to come' },
        ].map((item, i) => (
          <Card key={i} className="text-center p-6">
            <item.icon className="w-12 h-12 text-[#1F5F3A] mx-auto mb-4" />
            <h3 className="font-semibold text-[#0A2F1F] mb-2">{item.title}</h3>
            <p className="text-sm text-[#4A6356]">{item.desc}</p>
          </Card>
        ))}
      </div>

      <div className="bg-white rounded-xl p-8 mb-16">
        <h2 className="text-2xl font-['Playfair_Display'] font-bold text-[#1F5F3A] mb-6 text-center">
          Our Partners
        </h2>
        <div className="flex flex-wrap justify-center gap-12 items-center opacity-60">
          <div className="text-center">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <Building2 className="w-10 h-10" />
            </div>
            <p className="text-sm">Historic Scotland</p>
          </div>
          <div className="text-center">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <Users className="w-10 h-10" />
            </div>
            <p className="text-sm">Veterans UK</p>
          </div>
          <div className="text-center">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <Leaf className="w-10 h-10" />
            </div>
            <p className="text-sm">RHS Scotland</p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const ImpactPage = () => {
  const [stats, setStats] = useState(null);
  const [diary, setDiary] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, diaryRes] = await Promise.all([
          axios.get(`${API}/impact`),
          axios.get(`${API}/diary`)
        ]);
        setStats(statsRes.data);
        setDiary(diaryRes.data);
      } catch (e) {
        console.error('Error:', e);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="min-h-screen bg-[#F7F9F7]" data-testid="impact-page">
      <div className="bg-white border-b py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl lg:text-4xl font-['Playfair_Display'] font-bold text-[#1F5F3A] mb-4">
            Impact Tracker
          </h1>
          <p className="text-[#4A6356] max-w-2xl">
            See how your contributions are making a difference in real-time
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Progress */}
        <Card className="mb-12 p-8">
          <ProgressBar stats={stats} />
        </Card>

        {/* Stats */}
        <ImpactStats stats={stats} />

        {/* Build Diary */}
        <div className="mt-16">
          <h2 className="text-2xl font-['Playfair_Display'] font-bold text-[#1F5F3A] mb-8">
            Build Diary
          </h2>
          
          {diary.length === 0 ? (
            <Card className="p-8 text-center">
              <Calendar className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <p className="text-[#8C9E95]">Build diary updates coming soon!</p>
            </Card>
          ) : (
            <div className="space-y-6">
              {diary.map(entry => (
                <Card key={entry.id} className="p-6">
                  <div className="flex gap-4">
                    {entry.image_url && (
                      <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0">
                        <img src={entry.image_url} alt="" className="w-full h-full object-cover" />
                      </div>
                    )}
                    <div>
                      <Badge className="mb-2">{new Date(entry.created_at).toLocaleDateString()}</Badge>
                      <h3 className="font-semibold text-[#0A2F1F] mb-2">{entry.title}</h3>
                      <p className="text-sm text-[#4A6356]">{entry.content}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const RewardsPage = () => (
  <div className="min-h-screen bg-[#F7F9F7]" data-testid="rewards-page">
    <div className="bg-gradient-to-br from-[#C9A227] to-[#B08D21] text-white py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <Award className="w-16 h-16 mx-auto mb-6" />
        <h1 className="text-4xl lg:text-5xl font-['Playfair_Display'] font-bold mb-4">
          VG Points
        </h1>
        <p className="text-xl text-white/90 max-w-2xl mx-auto">
          Every £1 you donate earns 1 VG Point. Let's Grow Together!
        </p>
      </div>
    </div>

    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      {/* How It Works */}
      <div className="bg-white rounded-2xl p-8 mb-12 shadow-sm">
        <h2 className="text-2xl font-['Playfair_Display'] font-bold text-[#1F5F3A] mb-6 text-center">
          How VG Points Work
        </h2>
        <div className="grid md:grid-cols-3 gap-8">
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-[#1F5F3A] mx-auto mb-4 flex items-center justify-center text-white font-bold text-2xl">1</div>
            <h3 className="font-semibold text-[#0A2F1F] mb-2">Donate</h3>
            <p className="text-[#4A6356] text-sm">Every £1 donated = 1 VG Point automatically added to your account</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-[#1F5F3A] mx-auto mb-4 flex items-center justify-center text-white font-bold text-2xl">2</div>
            <h3 className="font-semibold text-[#0A2F1F] mb-2">Collect</h3>
            <p className="text-[#4A6356] text-sm">Points are tracked with your email and saved for when we open</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-[#1F5F3A] mx-auto mb-4 flex items-center justify-center text-white font-bold text-2xl">3</div>
            <h3 className="font-semibold text-[#0A2F1F] mb-2">Redeem</h3>
            <p className="text-[#4A6356] text-sm">Use points for money off when the greenhouses are open</p>
          </div>
        </div>
      </div>

      {/* Point Value */}
      <div className="bg-[#C9A227]/10 rounded-2xl p-8 mb-12 text-center border-2 border-[#C9A227]/30">
        <h2 className="text-3xl font-['Playfair_Display'] font-bold text-[#C9A227] mb-4">
          1 Point = 1p
        </h2>
        <p className="text-[#4A6356] text-lg max-w-xl mx-auto">
          Donate £100 and earn 100 VG Points worth £1 off your purchases. 
          The more you donate, the more you save when we open!
        </p>
      </div>

      {/* What Can You Get */}
      <h2 className="text-2xl font-['Playfair_Display'] font-bold text-[#1F5F3A] mb-8 text-center">
        What Can You Spend Points On?
      </h2>
      <div className="grid md:grid-cols-3 gap-8 mb-16">
        <Card className="text-center p-8">
          <div className="w-16 h-16 rounded-full bg-[#C9A227]/10 mx-auto mb-4 flex items-center justify-center">
            <Leaf className="w-8 h-8 text-[#C9A227]" />
          </div>
          <h3 className="font-semibold text-[#0A2F1F] text-lg mb-2">Plants</h3>
          <p className="text-[#4A6356] text-sm">
            Beautiful plants, flowers, and seedlings grown right here in our restored greenhouse
          </p>
        </Card>
        
        <Card className="text-center p-8">
          <div className="w-16 h-16 rounded-full bg-[#C9A227]/10 mx-auto mb-4 flex items-center justify-center">
            <Star className="w-8 h-8 text-[#C9A227]" />
          </div>
          <h3 className="font-semibold text-[#0A2F1F] text-lg mb-2">Fresh Veg</h3>
          <p className="text-[#4A6356] text-sm">
            Locally grown vegetables and produce from our gardens - fresh and seasonal
          </p>
        </Card>
        
        <Card className="text-center p-8">
          <div className="w-16 h-16 rounded-full bg-[#C9A227]/10 mx-auto mb-4 flex items-center justify-center">
            <Gift className="w-8 h-8 text-[#C9A227]" />
          </div>
          <h3 className="font-semibold text-[#0A2F1F] text-lg mb-2">Merchandise</h3>
          <p className="text-[#4A6356] text-sm">
            Veterans Garden branded items, gardening supplies, and exclusive supporter merchandise
          </p>
        </Card>
      </div>

      <Card className="p-8 bg-[#1F5F3A] text-white text-center">
        <h2 className="text-2xl font-['Playfair_Display'] font-bold mb-4">
          Coming Soon
        </h2>
        <p className="text-white/80 mb-6">
          Our rewards redemption system will launch when the greenhouses are built and up and running. Your points are being tracked and ready for you!
        </p>
        <Badge className="bg-white text-[#1F5F3A]">
          Points Never Expire
        </Badge>
      </Card>
    </div>
  </div>
);

const LeaderboardPage = () => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        const response = await axios.get(`${API}/leaderboard`);
        setLeaderboard(response.data);
      } catch (e) {
        console.error('Error:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchLeaderboard();
  }, []);

  return (
    <div className="min-h-screen bg-[#F7F9F7]" data-testid="leaderboard-page">
      <div className="bg-white border-b py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4">
            <Trophy className="w-10 h-10 text-[#C9A227]" />
            <div>
              <h1 className="text-3xl font-['Playfair_Display'] font-bold text-[#1F5F3A]">
                Leaderboard
              </h1>
              <p className="text-[#4A6356]">Our top restoration supporters</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {loading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <Card key={i} className="p-4 animate-pulse">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-gray-200 rounded-full" />
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-1/3 mb-2" />
                    <div className="h-3 bg-gray-200 rounded w-1/4" />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : leaderboard.length === 0 ? (
          <Card className="p-8 text-center">
            <Trophy className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <p className="text-[#8C9E95]">Be the first on the leaderboard!</p>
            <Button className="btn-primary mt-4" onClick={() => window.location.href = '/shop'}>
              Start Donating
            </Button>
          </Card>
        ) : (
          <div className="space-y-4">
            {leaderboard.map((donor, i) => (
              <Card key={i} className={`p-4 ${i < 3 ? 'border-[#C9A227] border-2' : ''}`}>
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                    i === 0 ? 'bg-[#C9A227] text-white' :
                    i === 1 ? 'bg-gray-300 text-gray-700' :
                    i === 2 ? 'bg-amber-600 text-white' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {i + 1}
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-[#0A2F1F]">{donor.donor_name}</p>
                    <p className="text-sm text-[#4A6356]">{donor.donation_count} donations</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-[#1F5F3A]">£{donor.total_donated?.toFixed(2)}</p>
                    <p className="text-sm text-[#C9A227]">{donor.total_points} pts</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const FAQPage = () => (
  <div className="min-h-screen bg-[#F7F9F7]" data-testid="faq-page">
    <div className="bg-white border-b py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-['Playfair_Display'] font-bold text-[#1F5F3A]">
          Frequently Asked Questions
        </h1>
      </div>
    </div>

    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Accordion type="single" collapsible className="space-y-4">
        {[
          { q: 'What happens to my donation?', a: 'Your donation directly funds the materials, equipment hire, and skilled labour needed to restore the greenhouse. We purchase exactly what you funded and use it on-site.' },
          { q: 'What are VG Points?', a: 'VG Points are our reward currency. You earn 1 point for every £1 donated. When the greenhouse opens, you can redeem points at our plant shop and coffee counter.' },
          { q: 'Can I claim Gift Aid?', a: 'Yes! If you\'re a UK taxpayer, you can add Gift Aid to your donation at checkout. This boosts your donation by 25% at no extra cost to you.' },
          { q: 'Will I receive a certificate?', a: 'Yes, every donor receives a digital PDF certificate with their name, items funded, and a unique certificate ID.' },
          { q: 'How can I track my impact?', a: 'Visit our Impact Tracker page to see real-time statistics on total raised, items funded, and build progress updates.' },
          { q: 'Is my payment secure?', a: 'Absolutely. We use Stripe for payment processing, which is PCI-compliant and supports card payments, Apple Pay, and Google Pay.' },
          { q: 'Can companies sponsor the project?', a: 'Yes! We offer corporate sponsorship packages with recognition on our donor wall, social media, and physical plaques. Contact us for details.' },
        ].map((faq, i) => (
          <AccordionItem key={i} value={`item-${i}`} className="bg-white rounded-lg border px-6">
            <AccordionTrigger className="text-left font-semibold text-[#0A2F1F]">
              {faq.q}
            </AccordionTrigger>
            <AccordionContent className="text-[#4A6356]">
              {faq.a}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </div>
);

const GalleryPage = () => {
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);

  // Initial gallery with your uploaded photos
  const defaultGallery = [
    {
      id: 1,
      title: "Current State of the Greenhouse",
      image_url: "https://customer-assets.emergentagent.com/job_vets-greenhouse-1/artifacts/mzkjg34o_IMG-20251210-WA0000.jpg",
      category: "Current State"
    },
    {
      id: 2,
      title: "Greenhouse Structure",
      image_url: "https://customer-assets.emergentagent.com/job_vets-greenhouse-1/artifacts/dh9veqgm_IMG-20251210-WA0003.jpg",
      category: "Current State"
    },
    {
      id: 3,
      title: "Restoration Site",
      image_url: "https://customer-assets.emergentagent.com/job_vets-greenhouse-1/artifacts/0cmta4sv_IMG-20251210-WA0004.jpg",
      category: "Current State"
    },
    {
      id: 4,
      title: "Historic Greenhouse Photo",
      image_url: "https://customer-assets.emergentagent.com/job_vets-greenhouse-1/artifacts/f7ohjieg_old%20photo%20of%20greenhouses.jpeg",
      category: "History"
    },
    {
      id: 5,
      title: "Weekend Work in Progress",
      image_url: "https://customer-assets.emergentagent.com/job_vets-greenhouse-1/artifacts/ky1fabd7_weekend%20work.jpeg",
      category: "Progress"
    }
  ];

  useEffect(() => {
    // Load gallery from API or use defaults
    const fetchGallery = async () => {
      try {
        const response = await axios.get(`${API}/gallery`);
        if (response.data && response.data.length > 0) {
          setGallery(response.data);
        } else {
          setGallery(defaultGallery);
        }
      } catch (e) {
        setGallery(defaultGallery);
      } finally {
        setLoading(false);
      }
    };
    fetchGallery();
  }, []);

  const categories = [...new Set(gallery.map(item => item.category))];
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedImage, setSelectedImage] = useState(null);

  const filteredGallery = selectedCategory === 'all' 
    ? gallery 
    : gallery.filter(item => item.category === selectedCategory);

  return (
    <div className="min-h-screen bg-[#F7F9F7]" data-testid="gallery-page">
      <div className="bg-[#1F5F3A] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-4">
            <Camera className="w-10 h-10 text-[#C9A227]" />
            <div>
              <h1 className="text-3xl lg:text-4xl font-['Playfair_Display'] font-bold">
                Gallery & Progress
              </h1>
              <p className="text-white/70">Follow our restoration journey</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 mb-8">
          <Button
            variant={selectedCategory === 'all' ? 'default' : 'outline'}
            className={selectedCategory === 'all' ? 'bg-[#1F5F3A]' : ''}
            onClick={() => setSelectedCategory('all')}
          >
            All Photos
          </Button>
          {categories.map(cat => (
            <Button
              key={cat}
              variant={selectedCategory === cat ? 'default' : 'outline'}
              className={selectedCategory === cat ? 'bg-[#1F5F3A]' : ''}
              onClick={() => setSelectedCategory(cat)}
            >
              {cat}
            </Button>
          ))}
        </div>

        {/* Gallery Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="aspect-[4/3] bg-gray-200 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredGallery.map((item) => (
              <div 
                key={item.id} 
                className="group cursor-pointer"
                onClick={() => setSelectedImage(item)}
              >
                <div className="aspect-[4/3] rounded-xl overflow-hidden bg-gray-100 relative">
                  <img 
                    src={item.image_url} 
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="absolute bottom-4 left-4 right-4">
                      <Badge className="bg-[#C9A227] mb-2">{item.category}</Badge>
                      <p className="text-white font-semibold">{item.title}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Lightbox */}
        <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
          <DialogContent className="max-w-4xl p-0 overflow-hidden">
            {selectedImage && (
              <>
                <img 
                  src={selectedImage.image_url} 
                  alt={selectedImage.title}
                  className="w-full h-auto"
                />
                <div className="p-6">
                  <Badge className="bg-[#C9A227] mb-2">{selectedImage.category}</Badge>
                  <DialogTitle className="font-['Playfair_Display'] text-xl text-[#1F5F3A]">
                    {selectedImage.title}
                  </DialogTitle>
                  {selectedImage.description && (
                    <DialogDescription className="mt-2">
                      {selectedImage.description}
                    </DialogDescription>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Call to Action */}
        <div className="mt-16 text-center">
          <Card className="p-8 bg-[#1F5F3A] text-white">
            <h2 className="text-2xl font-['Playfair_Display'] font-bold mb-4">
              Want to See More Progress?
            </h2>
            <p className="text-white/80 mb-6">
              Support the build and watch your contributions come to life. Every donation helps us move forward.
            </p>
            <Button 
              className="btn-accent"
              onClick={() => window.location.href = '/shop'}
            >
              Shop the Build
            </Button>
          </Card>
        </div>
      </div>
    </div>
  );
};

const ContactPage = () => (
  <div className="min-h-screen bg-[#F7F9F7]" data-testid="contact-page">
    <div className="bg-white border-b py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-['Playfair_Display'] font-bold text-[#1F5F3A]">
          Contact Us
        </h1>
        <p className="text-[#4A6356] mt-2">Get in touch with the Veterans Garden team</p>
      </div>
    </div>

    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="grid md:grid-cols-2 gap-12">
        <div>
          <h2 className="text-xl font-semibold text-[#0A2F1F] mb-6">Send us a message</h2>
          <Card className="p-6">
            <form className="space-y-4">
              <div>
                <Label>Your Name</Label>
                <Input placeholder="John Smith" />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" placeholder="john@example.com" />
              </div>
              <div>
                <Label>Message</Label>
                <textarea 
                  className="w-full min-h-[120px] px-3 py-2 border rounded-md resize-none"
                  placeholder="How can we help?"
                />
              </div>
              <Button className="btn-primary w-full">Send Message</Button>
            </form>
          </Card>
        </div>

        <div className="space-y-8">
          <div>
            <h2 className="text-xl font-semibold text-[#0A2F1F] mb-6">Contact Information</h2>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <MapPin className="w-6 h-6 text-[#1F5F3A] flex-shrink-0" />
                <div>
                  <p className="font-semibold text-[#0A2F1F]">Location</p>
                  <p className="text-[#4A6356]">Dumfries & Galloway, Scotland</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Mail className="w-6 h-6 text-[#1F5F3A] flex-shrink-0" />
                <div>
                  <p className="font-semibold text-[#0A2F1F]">Email</p>
                  <p className="text-[#4A6356]">veteransgarden@outlook.com</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Phone className="w-6 h-6 text-[#1F5F3A] flex-shrink-0" />
                <div>
                  <p className="font-semibold text-[#0A2F1F]">Phone</p>
                  <p className="text-[#4A6356]">07592 450871</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const CorporatePage = () => (
  <div className="min-h-screen bg-[#F7F9F7]" data-testid="corporate-page">
    <div className="bg-[#1F5F3A] text-white py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Badge className="mb-4 bg-[#C9A227]">For Businesses</Badge>
        <h1 className="text-4xl lg:text-5xl font-['Playfair_Display'] font-bold mb-4">
          Corporate Sponsorship
        </h1>
        <p className="text-xl text-white/80 max-w-2xl">
          Partner with us to make a lasting impact while showcasing your company's commitment to community and heritage
        </p>
      </div>
    </div>

    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div className="grid md:grid-cols-3 gap-8 mb-16">
        {[
          { level: 'Bronze', amount: '£5,000+', benefits: ['Logo on website', 'Social media mention', 'Certificate of partnership'] },
          { level: 'Silver', amount: '£15,000+', benefits: ['All Bronze benefits', 'Logo on donor wall', 'Event invitations', 'Press release mention'] },
          { level: 'Gold', amount: '£50,000+', benefits: ['All Silver benefits', 'Physical plaque in greenhouse', 'Naming opportunity', 'VIP opening event access'] },
        ].map((tier, i) => (
          <Card key={i} className={`p-6 ${i === 2 ? 'border-2 border-[#C9A227]' : ''}`}>
            <Badge className={i === 2 ? 'bg-[#C9A227]' : 'bg-[#1F5F3A]'} >{tier.level}</Badge>
            <p className="text-3xl font-['Playfair_Display'] font-bold text-[#1F5F3A] my-4">{tier.amount}</p>
            <ul className="space-y-2">
              {tier.benefits.map((benefit, j) => (
                <li key={j} className="flex items-center gap-2 text-sm text-[#4A6356]">
                  <Check className="w-4 h-4 text-[#3FA06A]" />
                  {benefit}
                </li>
              ))}
            </ul>
          </Card>
        ))}
      </div>

      <Card className="p-8 text-center">
        <h2 className="text-2xl font-['Playfair_Display'] font-bold text-[#1F5F3A] mb-4">
          Interested in Sponsoring?
        </h2>
        <p className="text-[#4A6356] mb-6">
          Contact our partnerships team to discuss custom sponsorship opportunities
        </p>
        <Button className="btn-primary" onClick={() => window.location.href = '/contact'}>
          Get in Touch
        </Button>
      </Card>
    </div>
  </div>
);

// =============================================================================
// ADMIN PAGES
// =============================================================================

const AdminLoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      if (response.data.is_admin) {
        localStorage.setItem('admin_token', response.data.token);
        localStorage.setItem('admin_name', response.data.name);
        toast.success('Welcome back!');
        navigate('/admin/dashboard');
      } else {
        toast.error('Admin access required');
      }
    } catch (e) {
      toast.error('Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F9F7] flex items-center justify-center py-12" data-testid="admin-login">
      <Card className="w-full max-w-md mx-4">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-[#1F5F3A] rounded-full mx-auto mb-4 flex items-center justify-center">
            <Building2 className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="font-['Playfair_Display'] text-2xl text-[#1F5F3A]">Admin Login</CardTitle>
          <CardDescription>Access the Veterans Garden dashboard</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input 
                id="email" 
                type="email" 
                value={email} 
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@vgreenhouse.org"
                required
                data-testid="admin-email"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input 
                id="password" 
                type="password" 
                value={password} 
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="admin-password"
              />
            </div>
            <Button type="submit" className="w-full btn-primary" disabled={loading} data-testid="admin-login-btn">
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [donations, setDonations] = useState([]);
  const [products, setProducts] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [editingProduct, setEditingProduct] = useState(null);
  const navigate = useNavigate();

  const token = localStorage.getItem('admin_token');
  const adminName = localStorage.getItem('admin_name');
  const authHeaders = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) {
      navigate('/admin');
      return;
    }
    loadData();
  }, [token, navigate]);

  const loadData = async () => {
    try {
      const [statsRes, donationsRes, productsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: authHeaders }),
        axios.get(`${API}/admin/donations?limit=100`, { headers: authHeaders }),
        axios.get(`${API}/products`)
      ]);
      setStats(statsRes.data);
      setDonations(donationsRes.data.donations || []);
      setProducts(productsRes.data);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin');
      }
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_name');
    navigate('/admin');
    toast.success('Logged out');
  };

  const exportCSV = async (giftAidOnly = false) => {
    try {
      const url = `${API}/admin/donations/export?format=csv${giftAidOnly ? '&gift_aid_only=true' : ''}`;
      const response = await axios.get(url, { 
        headers: authHeaders,
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `donations_${giftAidOnly ? 'giftaid_' : ''}${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success('CSV downloaded!');
    } catch (e) {
      toast.error('Failed to export');
    }
  };

  const deleteProduct = async (productId) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await axios.delete(`${API}/admin/products/${productId}`, { headers: authHeaders });
      toast.success('Product deleted');
      loadData();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const saveProduct = async (productData) => {
    try {
      if (editingProduct?.id) {
        await axios.put(`${API}/admin/products/${editingProduct.id}`, productData, { headers: authHeaders });
        toast.success('Product updated');
      } else {
        await axios.post(`${API}/admin/products`, productData, { headers: authHeaders });
        toast.success('Product created');
      }
      setEditingProduct(null);
      loadData();
    } catch (e) {
      toast.error('Failed to save product');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F9F7] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#1F5F3A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F9F7]" data-testid="admin-dashboard">
      {/* Header */}
      <div className="bg-[#1F5F3A] text-white py-4 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Building2 className="w-8 h-8" />
            <div>
              <h1 className="text-xl font-bold">Admin Dashboard</h1>
              <p className="text-white/70 text-sm">Welcome, {adminName}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="ghost" className="text-white hover:bg-white/10" onClick={() => window.open('/', '_blank')}>
              View Site
            </Button>
            <Button variant="outline" className="border-white/30 text-white hover:bg-white/10" onClick={handleLogout}>
              Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white shadow-sm">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="donations" data-testid="tab-donations">Donations</TabsTrigger>
            <TabsTrigger value="products" data-testid="tab-products">Products</TabsTrigger>
            <TabsTrigger value="export" data-testid="tab-export">Export / Gift Aid</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#3FA06A]/10 rounded-lg flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-[#3FA06A]" />
                    </div>
                    <div>
                      <p className="text-sm text-[#4A6356]">Total Raised</p>
                      <p className="text-2xl font-bold text-[#1F5F3A]">£{stats?.total_raised?.toLocaleString() || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#C9A227]/10 rounded-lg flex items-center justify-center">
                      <Heart className="w-6 h-6 text-[#C9A227]" />
                    </div>
                    <div>
                      <p className="text-sm text-[#4A6356]">Total Donations</p>
                      <p className="text-2xl font-bold text-[#1F5F3A]">{stats?.total_donations || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Gift className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm text-[#4A6356]">Gift Aid Claims</p>
                      <p className="text-2xl font-bold text-[#1F5F3A]">{stats?.gift_aid_count || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Package className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-[#4A6356]">Products</p>
                      <p className="text-2xl font-bold text-[#1F5F3A]">{stats?.total_products || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Recent Donations</CardTitle>
                <CardDescription>Last {Math.min(donations.length, 5)} donations received</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {donations.slice(0, 5).map((d, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-[#F7F9F7] rounded-lg">
                      <div>
                        <p className="font-semibold text-[#1F5F3A]">{d.donor_name}</p>
                        <p className="text-sm text-[#4A6356]">{d.donor_email}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-[#C9A227]">£{d.total_amount?.toFixed(2)}</p>
                        <div className="flex items-center gap-2">
                          {d.gift_aid && <Badge className="bg-purple-100 text-purple-700 text-xs">Gift Aid</Badge>}
                          <p className="text-xs text-[#8C9E95]">{d.created_at?.slice(0, 10)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Donations Tab */}
          <TabsContent value="donations">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>All Donations</CardTitle>
                    <CardDescription>{donations.length} completed donations</CardDescription>
                  </div>
                  <Button variant="outline" onClick={() => exportCSV(false)} data-testid="export-all-csv">
                    Export All CSV
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-2 font-semibold text-[#4A6356]">Date</th>
                        <th className="text-left py-3 px-2 font-semibold text-[#4A6356]">Donor</th>
                        <th className="text-left py-3 px-2 font-semibold text-[#4A6356]">Email</th>
                        <th className="text-left py-3 px-2 font-semibold text-[#4A6356]">Amount</th>
                        <th className="text-left py-3 px-2 font-semibold text-[#4A6356]">Gift Aid</th>
                        <th className="text-left py-3 px-2 font-semibold text-[#4A6356]">Items</th>
                      </tr>
                    </thead>
                    <tbody>
                      {donations.map((d, i) => (
                        <tr key={i} className="border-b hover:bg-[#F7F9F7]">
                          <td className="py-3 px-2">{d.created_at?.slice(0, 10)}</td>
                          <td className="py-3 px-2 font-medium">{d.donor_name}</td>
                          <td className="py-3 px-2 text-[#4A6356]">{d.donor_email}</td>
                          <td className="py-3 px-2 font-bold text-[#C9A227]">£{d.total_amount?.toFixed(2)}</td>
                          <td className="py-3 px-2">
                            {d.gift_aid ? (
                              <Badge className="bg-green-100 text-green-700">Yes</Badge>
                            ) : (
                              <Badge variant="outline">No</Badge>
                            )}
                          </td>
                          <td className="py-3 px-2 text-[#4A6356]">
                            {d.items?.slice(0, 2).map(item => `${item.name} x${item.quantity}`).join(', ')}
                            {d.items?.length > 2 && '...'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Products</CardTitle>
                    <CardDescription>Manage your shop items</CardDescription>
                  </div>
                  <Button className="btn-primary" onClick={() => setEditingProduct({})} data-testid="add-product">
                    <Plus className="w-4 h-4 mr-2" /> Add Product
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {products.map((p) => (
                    <div key={p.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="aspect-video bg-[#F7F9F7] rounded-lg mb-3 overflow-hidden">
                        {p.image_url ? (
                          <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Package className="w-8 h-8 text-[#8C9E95]" />
                          </div>
                        )}
                      </div>
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="font-semibold text-[#1F5F3A]">{p.name}</h4>
                          <p className="text-[#C9A227] font-bold">£{p.price?.toFixed(2)}</p>
                        </div>
                        {p.urgent && <Badge className="bg-red-100 text-red-700">Urgent</Badge>}
                      </div>
                      <p className="text-sm text-[#4A6356] mb-3 line-clamp-2">{p.description}</p>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => setEditingProduct(p)}>
                          Edit
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => deleteProduct(p.id)}>
                          Delete
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Product Edit Dialog */}
            <Dialog open={!!editingProduct} onOpenChange={(open) => !open && setEditingProduct(null)}>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>{editingProduct?.id ? 'Edit Product' : 'Add Product'}</DialogTitle>
                </DialogHeader>
                <ProductForm product={editingProduct} onSave={saveProduct} onCancel={() => setEditingProduct(null)} />
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Export Tab */}
          <TabsContent value="export">
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Gift className="w-5 h-5 text-purple-600" />
                    Gift Aid Export
                  </CardTitle>
                  <CardDescription>
                    Download donations with Gift Aid consent for HMRC submission
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <p className="text-sm text-purple-800">
                      <strong>{stats?.gift_aid_count || 0}</strong> donations with Gift Aid consent
                    </p>
                    <p className="text-sm text-purple-600 mt-1">
                      Potential claim: <strong>£{((stats?.total_raised || 0) * 0.25).toFixed(2)}</strong>
                    </p>
                  </div>
                  <Button className="w-full" onClick={() => exportCSV(true)} data-testid="export-giftaid-csv">
                    <Award className="w-4 h-4 mr-2" />
                    Download Gift Aid CSV
                  </Button>
                  <p className="text-xs text-[#8C9E95]">
                    CSV includes: donor name, email, address, amount, date - ready for HMRC Charities Online.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-[#1F5F3A]" />
                    Full Export
                  </CardTitle>
                  <CardDescription>
                    Download all donation records
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 bg-[#F0F5F2] rounded-lg">
                    <p className="text-sm text-[#4A6356]">
                      <strong>{stats?.total_donations || 0}</strong> total donations
                    </p>
                    <p className="text-sm text-[#1F5F3A] mt-1">
                      Total raised: <strong>£{(stats?.total_raised || 0).toLocaleString()}</strong>
                    </p>
                  </div>
                  <Button variant="outline" className="w-full" onClick={() => exportCSV(false)} data-testid="export-full-csv">
                    <Package className="w-4 h-4 mr-2" />
                    Download All Donations CSV
                  </Button>
                  <p className="text-xs text-[#8C9E95]">
                    Full export includes all completed donations with item details and VG points.
                  </p>
                </CardContent>
              </Card>
            </div>

            <Card className="mt-6">
              <CardHeader>
                <CardTitle>How to Claim Gift Aid</CardTitle>
              </CardHeader>
              <CardContent>
                <ol className="space-y-3 text-sm text-[#4A6356]">
                  <li className="flex gap-3">
                    <span className="w-6 h-6 bg-[#1F5F3A] text-white rounded-full flex items-center justify-center text-xs flex-shrink-0">1</span>
                    <span>Download the Gift Aid CSV using the button above</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="w-6 h-6 bg-[#1F5F3A] text-white rounded-full flex items-center justify-center text-xs flex-shrink-0">2</span>
                    <span>Log in to <a href="https://www.gov.uk/claim-gift-aid" target="_blank" rel="noopener noreferrer" className="text-[#1F5F3A] underline">HMRC Charities Online</a> with your charity credentials</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="w-6 h-6 bg-[#1F5F3A] text-white rounded-full flex items-center justify-center text-xs flex-shrink-0">3</span>
                    <span>Upload the CSV or manually enter donation details</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="w-6 h-6 bg-[#1F5F3A] text-white rounded-full flex items-center justify-center text-xs flex-shrink-0">4</span>
                    <span>HMRC will process and pay the 25% tax reclaim to your charity bank account</span>
                  </li>
                </ol>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

const ProductForm = ({ product, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: product?.name || '',
    description: product?.description || '',
    price: product?.price || '',
    category: product?.category || 'fixings',
    image_url: product?.image_url || '',
    urgent: product?.urgent || false
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      price: parseFloat(formData.price)
    });
  };

  const categories = [
    { id: 'glazing', name: 'Glazing & Frame' },
    { id: 'fixings', name: 'Fixings & Hardware' },
    { id: 'ventilation', name: 'Ventilation & Water' },
    { id: 'heating', name: 'Heating & Climate' },
    { id: 'electrical', name: 'Electrical' },
    { id: 'labour', name: 'Labour' },
    { id: 'bundles', name: 'Bundles & Funds' }
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label htmlFor="name">Product Name</Label>
        <Input 
          id="name" 
          value={formData.name} 
          onChange={(e) => setFormData({...formData, name: e.target.value})}
          required
        />
      </div>
      <div>
        <Label htmlFor="description">Description</Label>
        <Input 
          id="description" 
          value={formData.description} 
          onChange={(e) => setFormData({...formData, description: e.target.value})}
          required
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="price">Price (£)</Label>
          <Input 
            id="price" 
            type="number" 
            step="0.01"
            value={formData.price} 
            onChange={(e) => setFormData({...formData, price: e.target.value})}
            required
          />
        </div>
        <div>
          <Label htmlFor="category">Category</Label>
          <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {categories.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label htmlFor="image_url">Image URL</Label>
        <Input 
          id="image_url" 
          value={formData.image_url} 
          onChange={(e) => setFormData({...formData, image_url: e.target.value})}
          placeholder="https://..."
        />
      </div>
      <div className="flex items-center gap-2">
        <Checkbox 
          id="urgent" 
          checked={formData.urgent} 
          onCheckedChange={(checked) => setFormData({...formData, urgent: checked})}
        />
        <Label htmlFor="urgent" className="cursor-pointer">Mark as urgent need</Label>
      </div>
      <div className="flex gap-3 pt-4">
        <Button type="submit" className="flex-1 btn-primary">Save Product</Button>
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
      </div>
    </form>
  );
};

// =============================================================================
// APP
// =============================================================================

function App() {
  return (
    <CartProvider>
      <div className="App min-h-screen flex flex-col">
        <BrowserRouter>
          <Navbar />
          <main className="flex-1">
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/shop" element={<ShopPage />} />
              <Route path="/checkout" element={<CheckoutPage />} />
              <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
              <Route path="/gallery" element={<GalleryPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/impact" element={<ImpactPage />} />
              <Route path="/rewards" element={<RewardsPage />} />
              <Route path="/leaderboard" element={<LeaderboardPage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/corporate" element={<CorporatePage />} />
              <Route path="/admin" element={<AdminLoginPage />} />
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
            </Routes>
          </main>
          <Footer />
        </BrowserRouter>
        <Toaster position="bottom-right" richColors />
      </div>
    </CartProvider>
  );
}

export default App;
